import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';
import {
  Bell, Check, CheckCheck, Calendar, ClipboardList, Clock, Banknote,
  MessageCircle, Users, Megaphone, AlertCircle, Sparkles, UserPlus,
  FileWarning, CalendarCheck, CalendarX, ClipboardCheck, AlertTriangle,
  Trash2, Settings, X
} from 'lucide-react';
import { toast } from 'sonner';

// Icon mapping
const NOTIFICATION_ICONS = {
  // Leave
  leave_requested: Calendar,
  leave_approved: CalendarCheck,
  leave_rejected: CalendarX,
  leave_cancelled: Calendar,
  // Tasks
  task_assigned: ClipboardList,
  task_updated: ClipboardList,
  task_completed: ClipboardCheck,
  task_overdue: AlertTriangle,
  task_comment: MessageCircle,
  // Attendance
  attendance_reminder: Clock,
  attendance_checked_in: Clock,
  attendance_checked_out: Clock,
  // Payroll
  payroll_processed: Banknote,
  payslip_available: Banknote,
  // Chat
  new_message: MessageCircle,
  group_added: Users,
  // Calendar
  event_reminder: Bell,
  event_created: Calendar,
  // System
  announcement: Megaphone,
  system_alert: AlertCircle,
  welcome: Sparkles,
  // Employee
  employee_joined: UserPlus,
  profile_updated: UserPlus,
  contract_expiring: FileWarning,
};

// Priority colors
const PRIORITY_COLORS = {
  urgent: 'bg-red-500',
  high: 'bg-orange-500',
  normal: 'bg-blue-500',
  low: 'bg-gray-400',
};

// Category colors
const CATEGORY_COLORS = {
  leave: 'bg-purple-100 text-purple-800',
  task: 'bg-blue-100 text-blue-800',
  attendance: 'bg-green-100 text-green-800',
  payroll: 'bg-yellow-100 text-yellow-800',
  chat: 'bg-pink-100 text-pink-800',
  calendar: 'bg-indigo-100 text-indigo-800',
  system: 'bg-gray-100 text-gray-800',
  employee: 'bg-teal-100 text-teal-800',
};

const NotificationItem = ({ notification, onRead, onDelete, onNavigate }) => {
  const IconComponent = NOTIFICATION_ICONS[notification.type] || Bell;
  
  return (
    <div
      className={`p-3 border-b last:border-b-0 hover:bg-gray-50 transition-colors cursor-pointer ${
        !notification.is_read ? 'bg-blue-50/50' : ''
      }`}
      onClick={() => {
        if (!notification.is_read) onRead(notification.id);
        if (notification.action_url) onNavigate(notification.action_url);
      }}
    >
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div className={`p-2 rounded-full ${notification.is_read ? 'bg-gray-100' : 'bg-blue-100'}`}>
          <IconComponent className={`h-4 w-4 ${notification.is_read ? 'text-gray-500' : 'text-blue-600'}`} />
        </div>
        
        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h4 className={`text-sm font-medium truncate ${notification.is_read ? 'text-gray-700' : 'text-gray-900'}`}>
              {notification.title}
            </h4>
            {notification.priority === 'urgent' && (
              <span className="px-1.5 py-0.5 text-[10px] bg-red-100 text-red-700 rounded">Urgent</span>
            )}
          </div>
          <p className={`text-xs mt-0.5 line-clamp-2 ${notification.is_read ? 'text-gray-500' : 'text-gray-600'}`}>
            {notification.message}
          </p>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-[10px] text-gray-400">{notification.time_ago}</span>
            {notification.sender_name && (
              <span className="text-[10px] text-gray-400">• {notification.sender_name}</span>
            )}
          </div>
        </div>
        
        {/* Actions */}
        <div className="flex items-center gap-1">
          {!notification.is_read && (
            <div className="w-2 h-2 rounded-full bg-blue-500" />
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete(notification.id);
            }}
            className="p-1 hover:bg-gray-200 rounded opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <X className="h-3 w-3 text-gray-400" />
          </button>
        </div>
      </div>
    </div>
  );
};

const NotificationCenter = () => {
  const navigate = useNavigate();
  const { token, currentOffice, getAuthHeaders } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [filter, setFilter] = useState('all'); // all, unread
  const lastNotificationRef = useRef(null);
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    if (!token) return;
    
    try {
      const headers = getAuthHeaders();
      const params = new URLSearchParams({ limit: '50' });
      if (filter === 'unread') params.append('unread_only', 'true');
      
      const response = await fetch(`${API_URL}/api/notifications?${params}`, { headers });
      
      if (response.ok) {
        const data = await response.json();
        
        // Check for new notifications
        if (data.notifications.length > 0 && lastNotificationRef.current) {
          const newestId = data.notifications[0]?.id;
          if (newestId !== lastNotificationRef.current) {
            // Play sound for new notification
            playNotificationSound();
          }
        }
        
        if (data.notifications.length > 0) {
          lastNotificationRef.current = data.notifications[0]?.id;
        }
        
        setNotifications(data.notifications);
        setUnreadCount(data.unread_count);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  }, [token, API_URL, getAuthHeaders, filter]);

  // Fetch unread count only (for badge)
  const fetchUnreadCount = useCallback(async () => {
    if (!token) return;
    
    try {
      const headers = getAuthHeaders();
      const response = await fetch(`${API_URL}/api/notifications/unread-count`, { headers });
      
      if (response.ok) {
        const data = await response.json();
        
        // Show toast if new unread
        if (data.unread_count > unreadCount && unreadCount > 0) {
          toast.info(`You have ${data.unread_count} unread notifications`, {
            duration: 3000,
          });
        }
        
        setUnreadCount(data.unread_count);
      }
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  }, [token, API_URL, getAuthHeaders, unreadCount]);

  // Play notification sound
  const playNotificationSound = () => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 800;
      oscillator.type = 'sine';
      gainNode.gain.value = 0.1;
      
      oscillator.start();
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
      oscillator.stop(audioContext.currentTime + 0.3);
    } catch (e) {
      // Ignore audio errors
    }
  };

  // Mark as read
  const markAsRead = async (notificationId) => {
    try {
      const headers = getAuthHeaders();
      await fetch(`${API_URL}/api/notifications/${notificationId}/read`, {
        method: 'POST',
        headers,
      });
      
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      const headers = getAuthHeaders();
      await fetch(`${API_URL}/api/notifications/read-all`, {
        method: 'POST',
        headers,
      });
      
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
      toast.success('All notifications marked as read');
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  // Delete notification
  const deleteNotification = async (notificationId) => {
    try {
      const headers = getAuthHeaders();
      await fetch(`${API_URL}/api/notifications/${notificationId}`, {
        method: 'DELETE',
        headers,
      });
      
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  // Navigate to action URL
  const handleNavigate = (url) => {
    setIsOpen(false);
    navigate(url);
  };

  // Poll for notifications
  useEffect(() => {
    fetchNotifications();
    
    // Poll every 30 seconds for unread count
    const interval = setInterval(fetchUnreadCount, 30000);
    
    return () => clearInterval(interval);
  }, [fetchNotifications, fetchUnreadCount]);

  // Refetch when opened
  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen, fetchNotifications]);

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          data-testid="notification-bell"
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center text-[10px] font-bold text-white bg-red-500 rounded-full">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      
      <DropdownMenuContent align="end" className="w-96 p-0" data-testid="notification-dropdown">
        {/* Header */}
        <div className="p-3 border-b bg-gray-50">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">Notifications</h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={markAllAsRead}
                  className="text-xs h-7"
                >
                  <CheckCheck className="h-3 w-3 mr-1" />
                  Mark all read
                </Button>
              )}
            </div>
          </div>
          
          {/* Filter tabs */}
          <div className="flex gap-2 mt-2">
            <button
              onClick={() => setFilter('all')}
              className={`px-3 py-1 text-xs rounded-full transition-colors ${
                filter === 'all' 
                  ? 'bg-blue-100 text-blue-700' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setFilter('unread')}
              className={`px-3 py-1 text-xs rounded-full transition-colors ${
                filter === 'unread' 
                  ? 'bg-blue-100 text-blue-700' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Unread ({unreadCount})
            </button>
          </div>
        </div>
        
        {/* Notifications list */}
        <ScrollArea className="h-[400px]">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-32 text-gray-400">
              <Bell className="h-8 w-8 mb-2 opacity-50" />
              <p className="text-sm">No notifications</p>
            </div>
          ) : (
            <div className="group">
              {notifications.map(notification => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onRead={markAsRead}
                  onDelete={deleteNotification}
                  onNavigate={handleNavigate}
                />
              ))}
            </div>
          )}
        </ScrollArea>
        
        {/* Footer */}
        {notifications.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <div className="p-2">
              <Button
                variant="ghost"
                size="sm"
                className="w-full text-xs"
                onClick={() => {
                  setIsOpen(false);
                  navigate('/notifications');
                }}
              >
                View all notifications
              </Button>
            </div>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default NotificationCenter;
